/*
  RHP Cart Drawer
  ------------------------------------------------------------
  Refreshes via the Section Rendering API: fetch the section,
  swap [data-rhp-drawer-content]. All markup and money formatting
  stay in Liquid, which is why bundles render as one card without
  any DOM patching.

  This file loads once and lives outside the swapped node, so it
  survives refreshes (innerHTML never re-runs scripts).
*/
(function () {
  'use strict';

  var SECTION = 'rhp-cart-drawer';

  var TRIGGERS = [
    '[data-rhp-cart-open]',
    '#cart-icon-bubble', '.cart-icon-bubble',
    '[data-cart-sidebar]', '[data-cart-drawer-trigger]',
    '.header__icon--cart', '.js-drawer-open-cart',
    '.site-header__cart-toggle', '.header-cart', '.cart-link'
  ];

  var root, panel, busyEl;
  var busy = false;
  var lastFocus = null;

  function el() { return document.getElementById('RhpDrawer'); }

  function setBusy(on) {
    busy = on;
    if (busyEl) busyEl.hidden = !on;
  }

  function open() {
    if (!root) return;
    lastFocus = document.activeElement;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('rhp-dw-open');
    var f = panel.querySelector('button, a, input');
    if (f) setTimeout(function () { f.focus(); }, 320);
  }

  function close() {
    if (!root) return;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('rhp-dw-open');
    // If another overlay left the page locked, clear it rather than
    // stranding the visitor on a page that won't scroll.
    if (getComputedStyle(document.body).overflow === 'hidden' &&
        !document.querySelector('.is-open[data-rhp-drawer]')) {
      document.body.style.overflow = '';
    }
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function isOpen() { return root && root.classList.contains('is-open'); }

  /* ---------- refresh ---------- */
  var inFlight = null;

  function refresh() {
    if (inFlight) return inFlight;                 // coalesce
    inFlight = doRefresh().finally(function () { inFlight = null; });
    return inFlight;
  }

  function doRefresh() {
    return fetch('/?sections=' + SECTION, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data[SECTION];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-rhp-drawer-content]');
        var current = root.querySelector('[data-rhp-drawer-content]');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
        syncBadges();
      })
      .catch(function (e) { console.error('[rhp-drawer] refresh failed', e); });
  }

  /* Keep the header count in step with the drawer. */
  function syncBadges() {
    fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var sels = ['.cart-count-bubble span', '#cart-icon-bubble span', '[data-cart-count]',
                    '.cart-count', '.header__cart-count', '.site-header__cart-count'];
        sels.forEach(function (s) {
          document.querySelectorAll(s).forEach(function (n) {
            if (/^\s*\d+\s*$/.test(n.textContent) || n.hasAttribute('data-cart-count')) {
              n.textContent = cart.item_count;
            }
          });
        });
        document.dispatchEvent(new CustomEvent('rhp:drawer:refreshed',
          { bubbles: true, detail: { cart: cart } }));
      })
      .catch(function () {});
  }

  function post(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    });
  }

  function change(updates) {
    if (busy) return;
    setBusy(true);
    post('/cart/update.js', { updates: updates })
      .then(refresh)
      .finally(function () { setBusy(false); });
  }

  /* ---------- events inside the drawer ---------- */
  function wire() {
    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-rhp-dw-close]')) { close(); return; }

      /* Anything clicked inside the drawer root but outside the panel is
         a click on the backdrop. Testing for "not in the panel" rather
         than "is the scrim" means stacking or an overlay on top of the
         scrim can't break it. */
      if (!e.target.closest('.rhp-dw__panel')) { close(); return; }

      var qty = e.target.closest('[data-rhp-qty]');
      if (qty) {
        var u = {};
        u[qty.dataset.rhpQty] = parseInt(qty.dataset.to, 10);
        change(u);
        return;
      }

      var rm = e.target.closest('[data-rhp-remove-bundle]');
      if (rm) {
        var u2 = {};
        rm.dataset.rhpRemoveBundle.split(',').forEach(function (k) { if (k) u2[k] = 0; });
        change(u2);
        return;
      }

      var up = e.target.closest('[data-rhp-upsell]');
      if (up && !busy) {
        setBusy(true);
        post('/cart/add.js', { items: [{ id: Number(up.dataset.rhpUpsell), quantity: 1 }] })
          .then(refresh)
          .finally(function () { setBusy(false); });
        return;
      }

      if (e.target.closest('[data-rhp-checkout]')) {
        window.location.href = '/checkout';
        return;
      }

      if (e.target.closest('[data-rhp-disc-apply]')) {
        var input = root.querySelector('[data-rhp-disc-input]');
        var code = (input && input.value || '').trim();
        if (!code) return;
        // Shopify has no AJAX endpoint for discount codes. This applies it
        // and comes straight back, reopening the drawer on arrival.
        var back = window.location.pathname + (window.location.search || '');
        sessionStorage.setItem('rhpDrawerOpen', '1');
        window.location.href = '/discount/' + encodeURIComponent(code) +
                               '?redirect=' + encodeURIComponent(back);
      }
    });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && e.target.matches('[data-rhp-disc-input]')) {
        e.preventDefault();
        var b = root.querySelector('[data-rhp-disc-apply]');
        if (b) b.click();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) close();
    });
  }

  /* ---------- opening from anywhere on the page ---------- */
  function bindTriggers() {
    var extra = (root.dataset.trigger || '').split(',')
      .map(function (s) { return s.trim(); }).filter(Boolean);
    var list = extra.concat(TRIGGERS);

    document.addEventListener('click', function (e) {
      /* stopImmediatePropagation matters as much as preventDefault here.
         Without it the click carries on to the theme's own cart handler
         and Halo's sidebar opens alongside this one — two overlays, two
         scroll locks, a page that looks frozen. */
      for (var i = 0; i < list.length; i++) {
        if (e.target.closest(list[i])) {
          e.preventDefault();
          e.stopImmediatePropagation();
          refresh().then(open);
          return;
        }
      }
      var a = e.target.closest('a[href]');
      if (a) {
        var href = (a.getAttribute('href') || '').replace(/^https?:\/\/[^/]+/, '');
        if (/^\/(cart\/?)(\?.*)?$/.test(href)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          refresh().then(open);
        }
      }
    }, true);

    /* ---- Layer 1: take over add-to-cart forms outright ----
       Halo submits via jQuery and then navigates to /cart. A JS-driven
       navigation can't be cancelled after the fact, so the only reliable
       fix is to stop its handler ever running. A capture-phase listener
       on document fires before every bubble handler on the page. */
    if (root.dataset.intercept !== 'false') {
      var ATC_FORM = 'form[action*="/cart/add"]';

      function takeOver(e, form) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (busy) return;
        setBusy(true);
        /* Open straight away with the spinner over it. Waiting for the add
           AND the section render before showing anything meant two network
           round trips of dead time on every click. */
        open();
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          body: new FormData(form)          // keeps line item properties
        })
          .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
          .then(function (res) {
            if (!res.ok) throw new Error(res.d && res.d.description || 'Could not add that.');
            return refresh();
          })
          .catch(function (err) { close(); alert(err.message); })
          .finally(function () { setBusy(false); });
      }

      document.addEventListener('submit', function (e) {
        var form = e.target.closest && e.target.closest(ATC_FORM);
        if (form) takeOver(e, form);
      }, true);

      document.addEventListener('click', function (e) {
        var btn = e.target.closest('button[type="submit"], input[type="submit"]');
        if (!btn || btn.disabled) return;
        var form = btn.form || btn.closest(ATC_FORM);
        if (form && form.matches(ATC_FORM)) takeOver(e, form);
      }, true);
    }

    /* ---- Layer 2: catch every other cart write ----
       Halo uses jQuery ($.ajax -> XMLHttpRequest) in some paths and fetch
       in others, so both need watching. */
    var of = window.fetch;
    if (typeof of === 'function' && !window.__rhpDrawerPatched) {
      window.__rhpDrawerPatched = true;
      window.fetch = function () {
        var u = arguments[0];
        var href = typeof u === 'string' ? u : (u && u.url) || '';
        return of.apply(this, arguments).then(function (res) {
          if (!busy) reactTo(href, res.ok);
          return res;
        });
      };
    }

    var XHR = window.XMLHttpRequest;
    if (XHR && XHR.prototype && !window.__rhpXhrPatched) {
      window.__rhpXhrPatched = true;
      var openFn = XHR.prototype.open;
      XHR.prototype.open = function (method, url) {
        this.__rhpUrl = url;
        return openFn.apply(this, arguments);
      };
      var sendFn = XHR.prototype.send;
      XHR.prototype.send = function () {
        var xhr = this;
        xhr.addEventListener('load', function () {
          if (!busy) reactTo(xhr.__rhpUrl || '', xhr.status >= 200 && xhr.status < 300);
        });
        return sendFn.apply(this, arguments);
      };
    }

    function reactTo(href, ok) {
      if (/\/cart\/add(\.js)?/.test(href)) {
        if (ok) { setBusy(true); open(); refresh().finally(function () { setBusy(false); }); }
      } else if (/\/cart\/(change|update|clear)(\.js)?/.test(href)) {
        refresh();
      }
    }

    // Repairs from the bundle watcher, and anything else announcing a change.
    ['rhp:bundle:repaired', 'cart:refresh', 'cart:updated'].forEach(function (n) {
      document.addEventListener(n, function () { if (!busy) setTimeout(refresh, 150); });
    });
  }

  function init() {
    root = el();
    if (!root || root.dataset.dwInit === '1') return;
    root.dataset.dwInit = '1';
    panel = root.querySelector('.rhp-dw__panel');
    busyEl = root.querySelector('[data-rhp-dw-busy]');

    wire();
    bindTriggers();

    // Layer 3: if anything still lands on /cart, show the drawer over it.
    if (root.dataset.takeoverCartPage !== 'false' &&
        /^\/cart\/?$/.test(window.location.pathname)) {
      setTimeout(open, 120);
    }

    // Coming back from applying a discount code.
    if (sessionStorage.getItem('rhpDrawerOpen')) {
      sessionStorage.removeItem('rhpDrawerOpen');
      setTimeout(function () { refresh().then(open); }, 220);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
